import "../styles/AboutSerene.css";
import { motion, AnimatePresence } from "framer-motion";
import { useState } from "react";
import { X } from "lucide-react";

import Lightbox from "yet-another-react-lightbox";
import "yet-another-react-lightbox/styles.css";

import Zoom from "yet-another-react-lightbox/plugins/zoom";
import Counter from "yet-another-react-lightbox/plugins/counter";
import Fullscreen from "yet-another-react-lightbox/plugins/fullscreen";

import "yet-another-react-lightbox/plugins/counter.css";

import { palmsFeatures as features } from "../data/PalmsConstructionData";

// ----------------- ANIMATIONS -----------------
const container = {
  hidden: {},
  show: { transition: { staggerChildren: 0.25, delayChildren: 0.2 } },
};

const card3D = {
  hidden: { opacity: 0, y: 60, rotateY: 12, scale: 0.96 },
  show: {
    opacity: 1,
    y: 0,
    rotateY: 0,
    scale: 1,
    transition: { duration: 0.9, ease: [0.22, 1, 0.36, 1] },
  },
};

const fadeUp = {
  hidden: { opacity: 0, y: 45, filter: "blur(10px)" },
  show: {
    opacity: 1,
    y: 0,
    filter: "blur(0px)",
    transition: { duration: 0.9, ease: "easeOut" },
  },
};

const wordContainer = {
  hidden: {},
  show: {
    transition: {
      staggerChildren: 0.12,
      delayChildren: 0.15,
    },
  },
};

const wordVariant = {
  hidden: { y: 35, opacity: 0, filter: "blur(6px)" },
  show: {
    y: 0,
    opacity: 1,
    filter: "blur(0px)",
    transition: { duration: 0.8, ease: "easeOut" },
  },
};

const line1 = "Construction Progress".split(" ");
const line2 = "".split(" ");

// ================= COMPONENT =================
export default function ConstructionProgressPalms() {
  const [open, setOpen] = useState(false);
  const [slides, setSlides] = useState([]);
  const [index, setIndex] = useState(0);

  // New state for folders
  const [isFolderModalOpen, setIsFolderModalOpen] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);

  // ---------- OPEN FOLDERS ----------
  const handleFeatureClick = (item) => {
    setSelectedCategory(item);
    setIsFolderModalOpen(true);
  };

  // ---------- OPEN GALLERY ----------
  const openGallery = (folder) => {
    if (!folder.gallery?.length) return;

    // Format gallery slides properly based on whether they are strings (images) or objects (videos)
    const formattedSlides = folder.gallery.map((item) => {
      if (typeof item === "string") {
        return { type: "image", src: item };
      }
      return item;
    });

    setSlides(formattedSlides);
    setIndex(0);
    setOpen(true);
  };

  // ---------- CUSTOM SLIDE RENDERER (FOR VIDEO) ----------
  const renderSlide = ({ slide, offset }) => {
    if (slide.type === "video") {
      const shouldLoadVideo = offset === undefined || offset === 0;

      return (
        <div
          style={{
            width: "100%",
            height: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: "20px",
            boxSizing: "border-box",
          }}
        >
          <div
            style={{
              width: "80%",
              maxWidth: "1000px",
              background: "rgba(255, 255, 255, 0.05)",
              backdropFilter: "blur(10px)",
              WebkitBackdropFilter: "blur(10px)",
              border: "1px solid rgba(255, 255, 255, 0.1)",
              borderRadius: "24px",
              padding: "24px",
              boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.5)",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: "20px",
            }}
          >
            {slide.title ? (
              <div
                style={{
                  color: "#fff",
                  fontSize: "18px",
                  fontWeight: 600,
                  letterSpacing: "0.1em",
                  textTransform: "uppercase",
                  textAlign: "center",
                  background: "linear-gradient(90deg, #fff, #aaa)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                }}
              >
                {slide.title}
              </div>
            ) : null}
            <div style={{ width: "100%", borderRadius: "16px", overflow: "hidden", backgroundColor: "#000" }}>
              <video
                src={shouldLoadVideo ? slide.src : undefined}
                preload="none"
                controls
                controlsList="nodownload noplaybackrate"
                disablePictureInPicture
                disableRemotePlayback
                style={{
                  width: "100%",
                  display: "block",
                  objectFit: "contain",
                }}
              />
            </div>
          </div>
        </div>
      );
    }
    return null; // images handled automatically
  };

  return (
    <motion.section
      className="about-serene py-5"
      initial="hidden"
      whileInView="show"
      viewport={{ once: true, amount: 0.3 }}
      variants={container}
    >
      <div className="container py-3">

        <div className="row mb-5">
          <div className="col-lg-5 d-flex align-items-center justify-content-center">
            <div className="mp-text-block">

              <motion.p className="about-text text-black" variants={fadeUp}>
                Development at Palms is progressing steadily with high-quality standards and timely execution, ensuring a well-built and dependable living environment.
              </motion.p>


            </div>
          </div>


          <div className="col-lg-7 d-flex align-items-center justify-content-end mt-4 mt-lg-0">
            <motion.h1
              className="sage-title display-4"
              variants={wordContainer}
              initial="hidden"
              whileInView="show"
              viewport={{ once: false, amount: 0.6 }}
            >
              {line1.map((word, i) => (
                <motion.span
                  key={`line1-${i}`}
                  variants={wordVariant}
                  style={{ display: "inline-block", marginRight: "0.35em" }}
                >
                  {word}
                </motion.span>
              ))}
              <br />
              {line2.map((word, i) => (
                <motion.span
                  key={`line2-${i}`}
                  variants={wordVariant}
                  style={{ display: "inline-block", marginRight: "0.35em" }}
                >
                  {word}
                </motion.span>
              ))}
            </motion.h1>
          </div>
        </div>

        {/* ================= GRID ================= */}
        <motion.div className="row g-4" variants={container}>
          {features.map((item, i) => (
            <motion.div
              className="col-md-6 col-lg-6"
              key={i}
              variants={card3D}
              whileHover={{ scale: 1.03 }}
            >
              <div className="feature-card" onClick={() => handleFeatureClick(item)}>
                <img
                  src={item.cover}
                  alt={item.title}
                  className="img-fluid rounded-2 feature-img"
                />
                <p className="feature-title mt-3">{item.title}</p>
              </div>
            </motion.div>
          ))}
        </motion.div>

      </div>

      {/* ================= FOLDER MODAL ================= */}
      <AnimatePresence>
        {isFolderModalOpen && selectedCategory && (
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 50 }}
            transition={{ duration: 0.4, ease: "easeOut" }}
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              backgroundColor: "#0e1a3b",
              zIndex: 9999,
              overflowY: "auto",
              padding: "40px 20px",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <div className="container" style={{ flex: 1, display: "flex", flexDirection: "column" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "40px" }}>
                <h2
                  style={{
                    color: "#fff",
                    fontSize: "2rem",
                    fontWeight: 300,
                    letterSpacing: "2px",
                    textTransform: "uppercase",
                    margin: 0,
                  }}
                >
                  {selectedCategory.title}
                </h2>
                <button
                  onClick={() => setIsFolderModalOpen(false)}
                  style={{
                    background: "rgba(255, 255, 255, 0.1)",
                    border: "none",
                    borderRadius: "50%",
                    width: "48px",
                    height: "48px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    cursor: "pointer",
                    color: "#fff",
                    transition: "all 0.3s ease",
                  }}
                  onMouseEnter={(e) => (e.currentTarget.style.background = "rgba(255, 255, 255, 0.2)")}
                  onMouseLeave={(e) => (e.currentTarget.style.background = "rgba(255, 255, 255, 0.1)")}
                >
                  <X size={24} />
                </button>
              </div>

              <div className="row g-4">
                {selectedCategory.folders.map((folder, i) => (
                  <motion.div
                    className="col-md-6 col-lg-4"
                    key={i}
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.5, delay: i * 0.1 }}
                    whileHover={{ scale: 1.03 }}
                  >
                    <div
                      style={{ cursor: "pointer", borderRadius: "16px", overflow: "hidden", background: "#1e79c3" }}
                      onClick={() => openGallery(folder)}
                    >
                      <img
                        src={folder.cover}
                        alt={folder.title}
                        style={{ width: "100%", height: "250px", objectFit: "cover", display: "block" }}
                      />
                      <div style={{ padding: "20px" }}>
                        <p style={{ color: "#fff", margin: 0, fontSize: "1.1rem", fontWeight: 500 }}>
                          {folder.title}
                        </p>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ================= LIGHTBOX POPUP ================= */}
      <Lightbox
        open={open}
        close={() => setOpen(false)}
        slides={slides}
        index={index}

        /* Plugins */
        plugins={[Zoom, Counter, Fullscreen]}

        /* Image display */
        imageFit="contain"

        /* Zoom behavior */
        zoom={{
          maxZoomPixelRatio: 4,     // how much user can zoom
          zoomInMultiplier: 1.5,    // zoom speed
          doubleTapDelay: 300,
          doubleClickDelay: 300,
        }}

        /* Track slide change */
        on={{ view: ({ index }) => setIndex(index) }}

        /* Custom renderer */
        render={{
          slide: renderSlide,
          buttonPrev: slides.length <= 1 ? () => null : undefined,
          buttonNext: slides.length <= 1 ? () => null : undefined,
        }}

        /* Counter position */
        counter={{
          container: {
            style: {
              top: "16px",
              left: "16px",
              right: "auto",
              bottom: "auto",
              fontSize: "14px",
            },
          },
        }}

        /* Background */
        styles={{
          container: { backgroundColor: "rgba(0,0,0,0.96)" },
        }}
      />


    </motion.section>
  );
}
